"use client";

import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import { useForm } from "@mantine/form";
import { extractVariables, PREDEFINED_VARIABLES } from "@bi-studio/shared/lib";
import type {
    DataSource,
    ReportColumn,
    ReportQueryResult,
    ReportUpdate,
    ReportVariable,
} from "@bi-studio/shared/lib";
import {
    getReport,
    updateReport,
    previewReport,
    getDataSources,
    getSchema,
} from "@bi-studio/shared/apis";
import { TableSchema } from "@bi-studio/shared";
import { PageActions, PageContainer, ReportEditActionsButton } from "@/components";
import { ReportEditView } from "@/components/reports";
import { PreviewVariablesModal } from "@/components/reports/modals/PreviewVariablesModal";

export default function EditReportPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const { id: reportId } = useParams();

    const [dataSources, setDataSources] = React.useState<DataSource[]>([]);
    const [schema, setSchema] = React.useState<TableSchema[]>([]);
    const [isLoadingReport, setIsLoadingReport] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [activeTab, setActiveTab] = React.useState<string | null>("general");
    const [errorToFix, setErrorToFix] = React.useState<string | null>(null);

    const [previewResult, setPreviewResult] = React.useState<ReportQueryResult | null>(null);
    const [isPreviewing, setIsPreviewing] = React.useState(false);
    const [previewVariablesModalOpened, setPreviewVariablesModalOpened] = React.useState(false);
    const [previewVariableValues, setPreviewVariableValues] = React.useState<Record<string, string>>({});

    const form = useForm<ReportUpdate>({
        initialValues: {
            name: "",
            description: "",
            dataSourceId: "",
            query: "",
            variables: [],
            columns: [],
            allowedRoles: [],
        },
        validate: {
            name: (value) => (value?.trim() ? null : "Report name is required"),
            dataSourceId: (value) => (value ? null : "Data source is required"),
            query: (value) => (value?.trim() ? null : "SQL query is required"),
        },
    });

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    // Load initial data
    React.useEffect(() => {
        if (!token) return;
        getDataSources(token)
            .then(setDataSources)
            .catch((err) => console.error("Failed to load data sources:", err));
    }, [token]);

    // Load report details
    React.useEffect(() => {
        if (!token || !reportId) {
            setIsLoadingReport(false);
            return;
        }

        const loadReport = async () => {
            try {
                setIsLoadingReport(true);
                const data = await getReport(token, reportId);
                form.setValues({
                    name: data.name,
                    description: data.description || "",
                    dataSourceId: data.dataSourceId,
                    query: data.query,
                    variables: data.variables || [],
                    columns: data.columns || [],
                    allowedRoles: data.allowedRoles || [],
                });
                form.resetDirty();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load report");
            } finally {
                setIsLoadingReport(false);
            }
        };

        loadReport();
    }, [token, reportId]);

    // Load schema when data source changes
    React.useEffect(() => {
        if (!token || !form.values.dataSourceId) {
            setSchema([]);
            return;
        }

        getSchema(token, form.values.dataSourceId)
            .then(setSchema)
            .catch((err) => {
                console.error("Failed to load schema:", err);
                setSchema([]);
            });
    }, [token, form.values.dataSourceId]);

    // Scan variables when query changes
    const scanVariables = React.useCallback((currentQuery: string) => {
        if (!currentQuery) return;
        const detectedNames = extractVariables(currentQuery);

        const currentVars = form.values.variables || [];
        const existingMap = new Map(currentVars.map(v => [v.name, v]));

        const newVariables: ReportVariable[] = detectedNames.map(name => {
            if (existingMap.has(name)) {
                return existingMap.get(name)!;
            }
            return {
                name,
                label: name,
                type: 'text',
            };
        });

        if (JSON.stringify(newVariables) !== JSON.stringify(currentVars)) {
            form.setFieldValue("variables", newVariables);
        }
    }, [form]);

    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            scanVariables(form.values.query || "");
        }, 1000);
        return () => clearTimeout(timeoutId);
    }, [form.values.query, scanVariables]);

    const runPreview = async (variableValues: Record<string, string> = {}) => {
        if (!token || !form.values.dataSourceId || !form.values.query) return;

        try {
            setIsPreviewing(true);
            setError(null);
            const cleanQuery = form.values.query.trim().replace(/;$/, "");
            const result = await previewReport(token, {
                dataSourceId: form.values.dataSourceId,
                query: cleanQuery,
                pagination: { page: 1, pageSize: 100 },
                variables: variableValues,
            });
            setPreviewResult(result);
            setPreviewVariablesModalOpened(false);

            // Update columns if detected
            const detectedColumns: ReportColumn[] = [];
            if (result.columns && result.columns.length > 0) {
                detectedColumns.push(...result.columns);
            } else if (result.rows && result.rows.length > 0) {
                const firstRow = result.rows[0];
                Object.keys(firstRow).forEach((key, idx) => {
                    detectedColumns.push({
                        name: key,
                        displayName: key,
                        dataType: 'string',
                        order: idx
                    });
                });
            }

            if (detectedColumns.length > 0) {
                const prevColumns = form.values.columns || [];
                const existingMap = new Map(prevColumns.map(c => [c.name, c]));
                const mergedColumns = detectedColumns.map(col => {
                    if (existingMap.has(col.name)) {
                        return existingMap.get(col.name)!;
                    }
                    return col;
                });
                form.setFieldValue("columns", mergedColumns);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to preview query");
        } finally {
            setIsPreviewing(false);
        }
    };

    const handlePreview = async () => {
        setErrorToFix(null);
        if (!form.values.dataSourceId || !form.values.query?.trim()) {
            setError("Please select a data source and enter a query");
            return;
        }

        const detectedVariables = extractVariables(form.values.query);
        const userVariables = detectedVariables.filter(v => !PREDEFINED_VARIABLES.includes(v));

        if (userVariables.length > 0) {
            setPreviewVariablesModalOpened(true);
            return;
        }

        await runPreview();
    };

    const handleSave = async () => {
        const validation = form.validate();
        if (validation.hasErrors) {
            const errorMessages = Object.entries(validation.errors)
                .map(([field, msg]) => `${field}: ${msg}`)
                .join(", ");
            
            notifications.show({
                title: "Validation Error",
                message: `Please fix the following: ${errorMessages}`,
                color: "red",
            });
            
            if (validation.errors.name || validation.errors.dataSourceId) {
                setActiveTab("general");
            } else if (validation.errors.query) {
                setActiveTab("query");
            }
            return;
        }

        if (!token || !reportId) return;

        if (!form.values.name?.trim() || !form.values.dataSourceId || !form.values.query?.trim()) {
            const missing = [];
            if (!form.values.name?.trim()) missing.push("Name");
            if (!form.values.dataSourceId) missing.push("Data Source");
            if (!form.values.query?.trim()) missing.push("Query");

            notifications.show({
                title: "Required Fields Missing",
                message: `Please fill in: ${missing.join(", ")}`,
                color: "red",
            });
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            const sanitizedVariables = (form.values.variables || []).map(v => {
                if (v.type === 'select' && v.selectConfig?.source === 'query' && v.selectConfig.query) {
                    return {
                        ...v,
                        selectConfig: {
                            ...v.selectConfig,
                            query: v.selectConfig.query.trim().replace(/;$/, "")
                        }
                    };
                }
                return v;
            });

            const reportData: ReportUpdate = {
                name: form.values.name,
                description: form.values.description || null,
                dataSourceId: form.values.dataSourceId,
                query: (form.values.query || "").trim().replace(/;$/, ""),
                allowedRoles: form.values.allowedRoles,
                variables: sanitizedVariables,
                columns: form.values.columns || [],
            };

            await updateReport(token, reportId, reportData);
            notifications.show({
                title: "Report Updated",
                message: "Changes have been saved successfully.",
                color: "green",
            });
            navigate(`/reports/${reportId}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update report");
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(`/reports/${reportId}`);
    };

    if (authLoading || isLoadingReport) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <LoadingScreen message="Loading report details..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) return null;

    if (error && !form.values.name) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <ErrorState 
                    title="Report Not Found"
                    message={error || undefined}
                    actionLabel="Back to Folders"
                    onAction={() => navigate('/folders')}
                />
            </AppLayout>
        );
    }

    return (
        <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
            <PageContainer>
                <PageActions 
                    title="Edit Report" 
                    onBack={handleCancel}
                >
                    <ReportEditActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && activeTab !== 'preview' && (
                    <ErrorState 
                        title="Failed to update report"
                        message={error || undefined}
                        onAction={() => setError(null)}
                    />
                )}

                <ReportEditView 
                    isSaving={isSaving}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    form={form}
                    dataSources={dataSources}
                    schema={schema}
                    error={activeTab === 'preview' ? error : null}
                    errorToFix={errorToFix}
                    setErrorToFix={setErrorToFix}
                    handlePreview={handlePreview}
                    isPreviewing={isPreviewing}
                    previewResult={previewResult}
                />

                <PreviewVariablesModal
                    opened={previewVariablesModalOpened}
                    onClose={() => setPreviewVariablesModalOpened(false)}
                    variables={form.values.variables || []}
                    values={previewVariableValues}
                    onValuesChange={setPreviewVariableValues}
                    onConfirm={() => runPreview(previewVariableValues)}
                    dataSourceId={form.values.dataSourceId || ""}
                    loading={isPreviewing}
                />
            </PageContainer>
        </AppLayout>
    );
}
